/**
 * `data-screen-label="Agent"`, tab `channels` — **Confirm who's calling** (plan-029 WAPP-CONF; design §40.8.2,
 * §40.13.1, §40.13.2, §40.19.5, §40.25.1; D-7, D-9 as changed at AP-0; ADR 0082, proposed). The panel is inside an
 * existing screen, so it carries no `data-screen-label` (design §40.13.1).
 *
 * ```
 * Confirm who's calling                                                 [Not available yet]
 *   Ask the caller to confirm who they are before the agent uses
 *     [switch] Questions about your data           (one per capability the agent_template declares)
 *   How callers confirm
 *     (•) One-time code by text message   "€… per code sent, charged to your balance" · "From …: €… per code"
 *     ( ) Passcode                          "A passcode is weaker than a text message code. Use 6 to 8 digits."
 *   [Save] [Discard]   refusals, "Saved"
 * ```
 *
 * WHAT CAN BE PROTECTED is the agent_template's declaration, as the api answers it (`protectableCapability`); nothing
 * else is offered. The panel is hidden when the template declares nothing and no access level asks for a confirmation
 * (design §40.13.1); when the access level does ("number + text message code" or "number + passcode" under Who may call
 * this agent), the panel says so instead of offering a checklist.
 *
 * THE PRICE NEXT TO "SMS CODE" (design §40.25.1) is the api's `confirmationCodePrice`, formatted with `formatMoney` —
 * never a figure in this source; while none is set the method says "Not available yet" and cannot be chosen.
 *
 * THE SETTING LIVES ON THE VOICEAGENT, BESIDE THE VERSION (D-2): a save applies from the next call with `If-Match` on the
 * revision read; `409 revision_stale` reloads it and says so. Turning every capability off is always allowed.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@wetalk/api-client";
import { Button, Icon, Radio, Tag } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import "../../new/answering.css";
import "../../../billing/billing.css";
import "./caller-protection-panel.css";
import "./confirmation-panel.css";

import { apiErrorText } from "../../../../api/api-error.js";
import { useGateway } from "../../../../api/provider.js";
import { PartyRefusedError } from "../../../../api/slice/caller-protection.js";
import { useVoiceAgentAccess, type PartyAccessView } from "../../../../api/slice/party-access.js";
import {
  PARTY_CONFIRMATION_QUERY_KEY,
  PROTECTED_CAPABILITY,
  useVoiceAgentPartyConfirmation,
  type ConfirmationCodePriceView,
  type PartyConfirmationChange,
  type PartyConfirmationMinimum,
  type PartyConfirmationSettingView,
  type ProtectedCapability,
} from "../../../../api/slice/party-confirmation.js";
import { portalFormat, portalText, usePortalTranslation } from "../../../../i18n/portal.js";
import { formatMoney } from "../../../../money.js";
import { GLYPH } from "../../../../shell/glyph.js";
import { useMemberCan } from "../../../../shell/member-capability.js";
import { SwitchControl } from "../../../billing/switch-control.js";

export interface ConfirmationPanelProps {
  /** The VoiceAgent whose settings the panel shows. */
  readonly voiceAgentId: string;
  /** The VoiceAgent's agent_template (its audience and its declaration); `null` while unread. */
  readonly agentTemplateCode: string | null;
}

/** A capability in the portal's words. */
export function protectedCapabilityText(capability: ProtectedCapability | "every_tool"): string {
  switch (capability) {
    case "bi_query":
      return portalText("partyConfirmation", "capability.bi_query");
    case "caller_recall":
      return portalText("partyConfirmation", "capability.caller_recall");
    case "connection_operation":
      return portalText("partyConfirmation", "capability.connection_operation");
    case "every_tool":
      return portalText("partyConfirmation", "capability.every_tool");
  }
}

/** A minimum method in the portal's words. */
export function confirmationMinimumText(minimum: PartyConfirmationMinimum): string {
  switch (minimum) {
    case "sms_code":
      return portalText("partyConfirmation", "method.sms_code");
    case "sms_code_or_signed_visitor":
      return portalText("partyConfirmation", "method.sms_code_or_signed_visitor");
    case "signed_visitor":
      return portalText("partyConfirmation", "method.signed_visitor");
    case "passcode":
      return portalText("partyConfirmation", "method.passcode");
  }
}

/** "€… per code sent, charged to your balance" — from the api's price, never a literal; `null` while none is set. */
export function confirmationCodePriceText(price: ConfirmationCodePriceView | null): string {
  if (price === null) return portalText("partyConfirmation", "method.sms_code_price_unset");
  return portalText("partyConfirmation", "method.sms_code_price", {
    price: formatMoney({ amountMinor: price.amountMinor, currency: price.currency }),
  });
}

/** "From 1 December 2026: €… per code", or `null` when no change is scheduled. */
export function confirmationCodeNextPriceText(price: ConfirmationCodePriceView | null, locale: "en" | "el"): string | null {
  if (price === null || price.next === null) return null;
  return portalText("partyConfirmation", "method.sms_code_price_next", {
    date: portalFormat(locale).date(price.next.effectiveFrom, { day: "numeric", month: "long", year: "numeric" }),
    price: formatMoney({ amountMinor: price.next.amountMinor, currency: price.next.currency }),
  });
}

/** Whether a method can be chosen now. */
export function confirmationMinimumAvailable(setting: PartyConfirmationSettingView, minimum: PartyConfirmationMinimum): boolean {
  const availability = setting.methodAvailability;
  switch (minimum) {
    case "sms_code":
      return availability.smsCode;
    case "sms_code_or_signed_visitor":
      return availability.smsCode && availability.signedVisitor;
    case "signed_visitor":
      return availability.signedVisitor;
    case "passcode":
      return availability.passcode;
  }
}

/**
 * The methods offered: a text message code and a passcode always (drawn unavailable when they are), the signed-visitor
 * methods only once signed-in website visitors are available; the stored minimum always.
 */
export function confirmationMinimumsOffered(setting: PartyConfirmationSettingView): readonly PartyConfirmationMinimum[] {
  const offered: PartyConfirmationMinimum[] = ["sms_code"];
  if (setting.methodAvailability.signedVisitor || setting.minimumMethod === "sms_code_or_signed_visitor") offered.push("sms_code_or_signed_visitor");
  if (setting.methodAvailability.signedVisitor || setting.minimumMethod === "signed_visitor") offered.push("signed_visitor");
  offered.push("passcode");
  return offered;
}

/** The minimum a first save names: the stored one, else a text message code when available, else a passcode. */
export function defaultConfirmationMinimum(setting: PartyConfirmationSettingView): PartyConfirmationMinimum {
  if (setting.minimumMethod !== null) return setting.minimumMethod;
  return setting.methodAvailability.smsCode ? "sms_code" : "passcode";
}

/** The access level's confirmation, when it asks for one (`number_sms_code`, `number_passcode`); else `null`. */
export function accessImpliedLevelText(access: PartyAccessView | undefined): string | null {
  if (access === undefined || !access.enabled) return null;
  if (access.assuranceLevel === "number_sms_code") return portalText("partyConfirmation", "access.level_number_sms_code");
  if (access.assuranceLevel === "number_passcode") return portalText("partyConfirmation", "access.level_number_passcode");
  return null;
}

/** A refused save in the portal's language. */
export function partyConfirmationRefusalTextOf(error: unknown): string {
  if (error instanceof ApiError) {
    switch (error.code) {
      case "party_confirmation_unavailable":
        return portalText("partyConfirmation", "refused.unavailable");
      case "capability_not_protectable":
        return portalText("partyConfirmation", "refused.capability");
      case "confirmation_code_price_unset":
        return portalText("partyConfirmation", "refused.price_unset");
      case "method_unavailable": {
        const reason = error instanceof PartyRefusedError ? error.detailReason : null;
        if (reason === "signed_visitor_off") return portalText("partyConfirmation", "refused.signed_visitor_off");
        if (reason === "signed_visitor_voice_binding") return portalText("partyConfirmation", "refused.signed_visitor_voice_binding");
        return portalText("partyConfirmation", "refused.sms_code_off");
      }
      default:
        break;
    }
  }
  return apiErrorText(error, portalText("partyConfirmation", "refused.other"));
}

interface Draft {
  readonly protectedCapability: readonly ProtectedCapability[];
  readonly minimumMethod: PartyConfirmationMinimum;
}

function draftOf(setting: PartyConfirmationSettingView): Draft {
  return { protectedCapability: setting.protectedCapability, minimumMethod: defaultConfirmationMinimum(setting) };
}

function sameDraft(left: Draft, right: Draft): boolean {
  return (
    left.minimumMethod === right.minimumMethod &&
    left.protectedCapability.length === right.protectedCapability.length &&
    left.protectedCapability.every((one) => right.protectedCapability.includes(one))
  );
}

/** What a save sends: the capabilities in the closed list's order, and the minimum. */
export function partyConfirmationChangeOf(draft: Draft): PartyConfirmationChange {
  return {
    protectedCapability: PROTECTED_CAPABILITY.filter((one) => draft.protectedCapability.includes(one)),
    minimumMethod: draft.minimumMethod,
  };
}

export function ConfirmationPanel({ voiceAgentId }: ConfirmationPanelProps): ReactNode {
  const { locale } = usePortalTranslation("partyConfirmation");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const canEdit = useMemberCan().can("voice_agent.write");
  const setting = useVoiceAgentPartyConfirmation(voiceAgentId);
  const access = useVoiceAgentAccess(voiceAgentId);
  const [edited, setEdited] = useState<Draft | null>(null);
  const [savedLine, setSavedLine] = useState<"saved" | "reloaded" | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (input: { readonly draft: Draft; readonly stored: PartyConfirmationSettingView }) =>
      gateway.changeVoiceAgentPartyConfirmation(voiceAgentId, partyConfirmationChangeOf(input.draft), input.stored.revision),
    onSuccess: (next) => {
      queryClient.setQueryData(PARTY_CONFIRMATION_QUERY_KEY.setting(voiceAgentId), next);
      setEdited(null);
      setRefusal(null);
      setSavedLine("saved");
    },
    onError: async (error) => {
      if (error instanceof ApiError && error.code === "revision_stale") {
        await queryClient.invalidateQueries({ queryKey: PARTY_CONFIRMATION_QUERY_KEY.setting(voiceAgentId) });
        setEdited(null);
        setRefusal(null);
        setSavedLine("reloaded");
        return;
      }
      setRefusal(partyConfirmationRefusalTextOf(error));
    },
  });

  const heading = <h2 className="ch-section-title">{portalText("partyConfirmation", "panel.title")}</h2>;

  if (setting.isError) {
    return (
      <>
        {heading}
        <p className="c-note c-note-critical" role="alert">
          {portalText("partyConfirmation", "panel.read_failed")}{" "}
          {apiErrorText(setting.error, portalText("partyConfirmation", "panel.reload"))}
        </p>
      </>
    );
  }
  if (setting.data === undefined) {
    return (
      <>
        {heading}
        <p className="c-note">{portalText("partyConfirmation", "panel.reading")}</p>
      </>
    );
  }

  const stored = setting.data;
  const implied = accessImpliedLevelText(access.data);
  // Hidden when the template declares nothing, nothing is stored and no access level asks for a confirmation.
  if (stored.protectableCapability.length === 0 && stored.protectedCapability.length === 0 && implied === null) return null;

  const base = draftOf(stored);
  const draft = edited ?? base;
  const changed = !sameDraft(draft, base);
  const locked = !canEdit || !stored.available;
  const offeredCapability = PROTECTED_CAPABILITY.filter(
    (one) => stored.protectableCapability.includes(one) || stored.protectedCapability.includes(one),
  );
  const nextPrice = confirmationCodeNextPriceText(stored.confirmationCodePrice, locale);
  const protecting = draft.protectedCapability.length > 0;

  const change = (next: Partial<Draft>): void => {
    setSavedLine(null);
    setRefusal(null);
    setEdited({ ...draft, ...next });
  };

  return (
    <>
      <div className="cp-head">
        {heading}
        {stored.available ? null : <Tag tone="neutral">{portalText("partyConfirmation", "panel.unavailable")}</Tag>}
      </div>
      <p className="ch-section-dek">{portalText("partyConfirmation", "panel.dek")}</p>

      <div className="ch-card cp pc" data-party-confirmation={stored.available ? "available" : "unavailable"}>
        {stored.available ? null : (
          <p className="cp-note" role="note">
            <Icon glyph={GLYPH.info} size="sm" />
            <span>{portalText("partyConfirmation", "panel.unavailable_note")}</span>
          </p>
        )}

        {implied === null ? null : (
          <p className="cp-note" role="note" data-access-implied="true">
            <Icon glyph={GLYPH.info} size="sm" />
            <span>{portalText("partyConfirmation", "panel.access_implied", { level: implied })}</span>
          </p>
        )}

        {offeredCapability.length === 0 ? null : (
          <>
            <div className="cp-row">
              <p className="ch-card-label ch-card-label-flush" id={`pc-capability-${voiceAgentId}`}>
                {portalText("partyConfirmation", "capability.label")}
              </p>
              <div className="pc-capability" role="group" aria-labelledby={`pc-capability-${voiceAgentId}`}>
                {offeredCapability.map((capability) => (
                  <SwitchControl
                    key={capability}
                    label={protectedCapabilityText(capability)}
                    checked={draft.protectedCapability.includes(capability)}
                    disabled={locked}
                    onChange={(on) => {
                      change({
                        protectedCapability: on
                          ? [...draft.protectedCapability, capability]
                          : draft.protectedCapability.filter((one) => one !== capability),
                      });
                    }}
                  />
                ))}
              </div>
              {protecting ? null : <span className="c-answer-note">{portalText("partyConfirmation", "capability.none_selected")}</span>}
            </div>

            <div className="cp-row">
              <p className="ch-card-label ch-card-label-flush" id={`pc-method-${voiceAgentId}`}>
                {portalText("partyConfirmation", "method.label")}
              </p>
              <div className="pc-method" role="radiogroup" aria-labelledby={`pc-method-${voiceAgentId}`}>
                {confirmationMinimumsOffered(stored).map((minimum) => {
                  const available = confirmationMinimumAvailable(stored, minimum);
                  const smsMethod = minimum === "sms_code" || minimum === "sms_code_or_signed_visitor";
                  return (
                    <div className="pc-method-option" key={minimum} data-minimum={minimum} data-available={available ? "true" : "false"}>
                      <Radio
                        name={`pc-minimum-${voiceAgentId}`}
                        value={minimum}
                        label={confirmationMinimumText(minimum)}
                        checked={draft.minimumMethod === minimum}
                        disabled={locked || (!available && stored.minimumMethod !== minimum)}
                        onChange={() => {
                          change({ minimumMethod: minimum });
                        }}
                      />
                      {smsMethod ? (
                        <span className="c-answer-note pc-price">
                          {available || stored.confirmationCodePrice !== null
                            ? confirmationCodePriceText(stored.confirmationCodePrice)
                            : portalText("partyConfirmation", "method.unavailable")}
                          {nextPrice === null ? null : <> · {nextPrice}</>}
                        </span>
                      ) : null}
                      {!smsMethod && !available ? (
                        <span className="c-answer-note">{portalText("partyConfirmation", "method.unavailable")}</span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
              {draft.minimumMethod === "sms_code" || draft.minimumMethod === "sms_code_or_signed_visitor" ? (
                <span className="c-answer-note">{portalText("partyConfirmation", "method.sms_code_note")}</span>
              ) : null}
              {draft.minimumMethod === "passcode" ? (
                <>
                  <span className="pc-warn" role="note">
                    {portalText("partyConfirmation", "method.passcode_warning")}
                  </span>
                  <span className="c-answer-note">{portalText("partyConfirmation", "method.passcode_note")}</span>
                </>
              ) : null}
            </div>
          </>
        )}

        {canEdit ? null : <p className="ch-card-foot">{portalText("partyConfirmation", "panel.read_only")}</p>}

        {changed || savedLine !== null || refusal !== null ? (
          <div className="ch-card-actions">
            {changed ? (
              <Button
                variant="primary"
                disabled={save.isPending || locked}
                onClick={() => {
                  setRefusal(null);
                  save.mutate({ draft, stored });
                }}
              >
                {save.isPending ? portalText("partyConfirmation", "panel.saving") : portalText("partyConfirmation", "panel.save")}
              </Button>
            ) : null}
            {changed ? (
              <Button
                variant="ghost"
                onClick={() => {
                  setEdited(null);
                  setRefusal(null);
                }}
              >
                {portalText("partyConfirmation", "panel.discard")}
              </Button>
            ) : null}
            {refusal === null ? null : (
              <span className="c-note c-note-critical" role="alert">
                {refusal}
              </span>
            )}
            {savedLine === null ? null : (
              <span className="ch-note" role="status">
                {savedLine === "saved"
                  ? portalText("partyConfirmation", "panel.saved")
                  : portalText("partyConfirmation", "panel.revision_stale")}
              </span>
            )}
          </div>
        ) : null}
      </div>
    </>
  );
}
